import {
  X,
  CheckCircle2,
  AlertCircle,
  Clock,
  ChevronDown,
  Users,
} from "lucide-react";
import React from "react";

interface SMSDetail {
  farmer_id: number;
  farmer_full_name: string;
  status_code: number;
  reason: string;
}

interface JobData {
  job_id: string;
  status: "pending" | "processing" | "completed" | "failed";
  total_poultries: number;
  processed_poultries: number;
  succeeded_poultries: number;
  failed_poultries: number;
  progress_percentage: number;
  started_at: string | null;
  completed_at: string | null;
  succeeded_poultry_ids: string[];
  sms_details: SMSDetail[];
  failed_poultry_ids: string[];
  message: string;
}

interface JobStatusModalProps {
  data: JobData;
  loadingModal: any;
  onClose: () => void;
  onRefresh: (data: any) => void;
}

interface StatBoxProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
}

interface DetailsSectionProps {
  title: string;
  items: string[];
  emptyMessage: string;
}

const JobStatusModal: React.FC<JobStatusModalProps> = ({
  data,
  onClose,
  onRefresh,
  loadingModal,
}) => {

  if (loadingModal)
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
        <div className="bg-white p-6 rounded-lg shadow-xl flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-gray-700">لطفاً منتظر بمانید...</p>
        </div>
      </div>
    );
  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-30 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[75vh] overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b dark:border-gray-700">
          <h3 className="text-lg font-bold dark:text-white">
            جزئیات وضعیت کار
            <button
              onClick={() => onRefresh(data?.job_id)}
              className="text-md px-4 bg-blue-700 text-gray-100  border mx-4 border-gray-700 rounded-md hover:bg-red-600 dark:text-gray-300 dark:hover:text-white"
              aria-label="Close modal"
            >
              بروزرسانی داده
            </button>
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
              <p className="text-sm text-gray-500 dark:text-gray-300">
                شناسه کار
              </p>
              <p className="font-medium dark:text-white">{data.job_id}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
              <p className="text-sm text-gray-500 dark:text-gray-300">وضعیت</p>
              <div className="flex items-center gap-2">
                {data.status === "completed" ? (
                  <CheckCircle2 size={16} className="text-green-500" />
                ) : data.status === "failed" ? (
                  <AlertCircle size={16} className="text-red-500" />
                ) : (
                  <Clock size={16} className="text-yellow-500" />
                )}
                <p className="font-medium dark:text-white">{data.status}</p>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm dark:text-gray-300">پیشرفت</span>
              <span className="text-sm font-medium dark:text-white">
                {data.progress_percentage}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
              <div
                className="bg-blue-600 h-2.5 rounded-full"
                style={{ width: `${data.progress_percentage}%` }}
              ></div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatBox
              title="کل طیور"
              value={data.total_poultries}
              icon={<Users size={16} />}
              color="bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-300"
            />
            <StatBox
              title="پردازش شده"
              value={data.processed_poultries}
              icon={<CheckCircle2 size={16} />}
              color="bg-green-100 text-green-600 dark:bg-green-900/50 dark:text-green-300"
            />
            <StatBox
              title="موفق"
              value={data.succeeded_poultries}
              icon={<CheckCircle2 size={16} />}
              color="bg-emerald-100 text-emerald-600 dark:bg-emerald-900/50 dark:text-emerald-300"
            />
            <StatBox
              title="ناموفق"
              value={data.failed_poultries}
              icon={<AlertCircle size={16} />}
              color="bg-red-100 text-red-600 dark:bg-red-900/50 dark:text-red-300"
            />
          </div>

          {/* Timeline */}
          <div className="space-y-2">
            <p className="text-sm font-medium dark:text-gray-300">زمانبندی</p>
            <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-300">
                  شروع
                </span>
                <div className="text-right">
                  <div className="text-sm dark:text-white">
                    {data.started_at
                      ? new Date(data.started_at).toLocaleDateString("fa-IR")
                      : "--"}
                  </div>
                  <div className="text-xs text-gray-400 dark:text-gray-300">
                    {data.started_at
                      ? new Date(data.started_at).toLocaleTimeString("fa-IR")
                      : "--"}
                  </div>
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-300">
                  پایان
                </span>
                <div className="text-right">
                  <div className="text-sm dark:text-white">
                    {data.completed_at
                      ? new Date(data.completed_at).toLocaleDateString("fa-IR")
                      : "--"}
                  </div>
                  <div className="text-xs text-gray-400 dark:text-gray-300">
                    {data.completed_at
                      ? new Date(data.completed_at).toLocaleTimeString("fa-IR")
                      : "--"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* SMS Details Section */}
          <details className="border dark:border-gray-700 rounded-lg overflow-hidden">
            <summary className="p-3 bg-gray-50 dark:bg-gray-700 flex justify-between items-center cursor-pointer">
              <span className="text-sm font-medium dark:text-gray-300">
                جزئیات ارسال پیامک
              </span>
              <ChevronDown
                size={16}
                className="text-gray-500 transition-transform duration-200"
              />
            </summary>
            <div className="p-3 bg-white dark:bg-gray-800 max-h-60 overflow-y-auto">
              {data.sms_details && data.sms_details.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          شناسه کشاورز
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          نام کامل
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          کد وضعیت
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          دلیل
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
                      {data.sms_details.map((detail, index) => (
                        <tr
                          key={index}
                          className="hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                            {detail.farmer_id}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                            {detail.farmer_full_name}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                            {detail.status_code}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                            {detail.reason || "نامشخص"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  هیچ جزئیاتی برای نمایش وجود ندارد
                </p>
              )}
            </div>
          </details>

          {/* Expandable Sections */}
          <DetailsSection
            title="شناسه طیور موفق"
            items={data.succeeded_poultry_ids}
            emptyMessage="طیور موفقی وجود ندارد"
          />

          <DetailsSection
            title="شناسه طیور ناموفق"
            items={data.failed_poultry_ids}
            emptyMessage="طیور ناموفقی وجود ندارد"
          />

          {/* Message */}
          {data.message && (
            <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
              <p className="text-sm font-medium dark:text-gray-300">پیام</p>
              <p className="text-sm mt-1 dark:text-white">{data.message}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StatBox: React.FC<StatBoxProps> = ({ title, value, icon, color }) => (
  <div className={`p-3 rounded-lg flex items-center gap-3 ${color}`}>
    <div className="p-1.5 rounded-full bg-white/50 dark:bg-black/20">
      {icon}
    </div>
    <div>
      <p className="text-xs">{title}</p>
      <p className="font-bold">{value}</p>
    </div>
  </div>
);

const DetailsSection: React.FC<DetailsSectionProps> = ({
  title,
  items,
  emptyMessage,
}) => (
  <details className="border dark:border-gray-700 rounded-lg overflow-hidden">
    <summary className="p-3 bg-gray-50 dark:bg-gray-700 flex justify-between items-center cursor-pointer">
      <span className="text-sm font-medium dark:text-gray-300">{title}</span>
      <ChevronDown
        size={16}
        className="text-gray-500 transition-transform duration-200"
      />
    </summary>
    <div className="p-3 bg-white dark:bg-gray-800 max-h-40 overflow-y-auto">
      {items && items.length > 0 ? (
        <ul className="space-y-1">
          {items.map((item, index) => (
            <li key={index} className="text-sm dark:text-gray-300">
              {item}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {emptyMessage}
        </p>
      )}
    </div>
  </details>
);

export default JobStatusModal;